import React, { useEffect, useMemo, useState } from "react";
import { api, fmtBRL, CATEGORIA_LABEL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart,
} from "recharts";
import {
  Printer, TrendingUp, Users, Package, DollarSign, Building2, Trees,
  Award, Sparkles, BarChart3, Activity, ShoppingBag, HardHat,
} from "lucide-react";

// Paleta institucional COOMFLONA (verde escuro + cores complementares)
const COL = {
  ink: "#0B2E1F",         // Verde institucional profundo
  primary: "#1E5D3F",     // Verde COOMFLONA
  accent: "#FF7A00",      // Laranja Amazônia
  gold: "#D4A017",        // Ouro
  leaf: "#5C9A6B",        // Verde folha
  clay: "#B85C25",        // Terracota
  soft: "#F4F1E8",        // Bege claro (papel)
  paper: "#FBFAF5",       // Papel off-white
  slate: "#334155",       // Cinza escuro
  muted: "#94A3B8",       // Cinza claro
};

const CHART_PALETTE = [COL.primary, COL.accent, COL.gold, COL.leaf, COL.clay, "#7C3AED", "#0EA5E9"];

const YEARS = () => {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2];
};

export default function AssembleiaReport() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    monthly: null,
    monthlyPurch: null,
    bySupplier: null,
    byCommunity: null,
    top: null,
    byCat: null,
    stock: null,
    epis: null,
    reqs: null,
  });

  const loadAll = async () => {
    setLoading(true);
    try {
      const start = `${year}-01-01`;
      const end = `${year}-12-31`;
      const [monthly, monthlyPurch, bySupplier, byCommunity, top, byCat, stock, epis, reqs] = await Promise.all([
        api.get(`/reports/monthly-summary?months=12`).catch(() => ({ data: [] })),
        api.get(`/reports/monthly-purchases`).catch(() => ({ data: [] })),
        api.get(`/reports/by-supplier`).catch(() => ({ data: [] })),
        api.get(`/reports/by-community`).catch(() => ({ data: [] })),
        api.get(`/reports/top-products`).catch(() => ({ data: [] })),
        api.get(`/reports/by-category`).catch(() => ({ data: [] })),
        api.get(`/reports/stock-current`).catch(() => ({ data: null })),
        api.get(`/reports/epi-by-collaborator`).catch(() => ({ data: [] })),
        api.get(`/reports/requisitions-report?start=${start}&end=${end}`).catch(() => ({ data: null })),
      ]);
      setData({
        monthly: monthly.data,
        monthlyPurch: monthlyPurch.data,
        bySupplier: bySupplier.data,
        byCommunity: byCommunity.data,
        top: top.data,
        byCat: byCat.data,
        stock: stock.data,
        epis: epis.data,
        reqs: reqs.data,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [year]);

  const kpis = useMemo(() => {
    const monthly = data.monthly || [];
    const monthlyPurch = data.monthlyPurch || [];
    const stock = data.stock || {};
    const byCommunity = data.byCommunity || [];
    const totalCompras = monthlyPurch.reduce((s, m) => s + (m.total || 0), 0);
    const totalMov = monthly.reduce((s, m) => s + (m.entrada_qtd || 0) + (m.saida_qtd || 0), 0);
    const totalReqs = monthly.reduce((s, m) => s + (m.requisicoes || 0), 0);
    const totalCooperados = byCommunity.reduce((s, c) => s + (c.qtd_compras || 0), 0);
    return {
      totalCompras,
      totalMov,
      totalReqs,
      totalCooperados,
      valorEstoque: stock.total_valor || 0,
      qtdProdutos: stock.total_produtos || 0,
      qtdComunidades: byCommunity.length,
      qtdFornecedores: (data.bySupplier || []).length,
    };
  }, [data]);

  const monthlyChart = useMemo(() => {
    return (data.monthly || []).map((m) => ({
      mes: m.mes_label?.slice(5) || m.mes_label,
      Entradas: m.entrada_valor || 0,
      Saidas: m.saida_valor || 0,
      Requisicoes: (m.requisicoes || 0) * 1000, // placeholder scaling avoided
    }));
  }, [data]);

  const purchaseChart = useMemo(() => {
    return (data.monthlyPurch || []).map((m) => ({
      mes: m.mes?.slice(5) || m.mes,
      Fornecedor: m.fornecedor || 0,
      Comunidade: m.comunidade || 0,
    }));
  }, [data]);

  const categoryChart = useMemo(() => {
    return (data.byCat || []).map((c) => ({
      name: CATEGORIA_LABEL[c.categoria] || c.categoria,
      value: c.value || 0,
      count: c.count || 0,
    }));
  }, [data]);

  const topProductsChart = useMemo(() => {
    return (data.top || []).slice(0, 10).map((p) => ({
      nome: p.nome.length > 24 ? p.nome.slice(0, 22) + "…" : p.nome,
      total: p.total,
    }));
  }, [data]);

  const communityChart = useMemo(() => {
    return (data.byCommunity || []).slice(0, 10).map((c) => ({
      nome: (c.comunidade || "").length > 22 ? c.comunidade.slice(0, 20) + "…" : c.comunidade,
      total: c.total_valor || 0,
      qtd: c.qtd_compras || 0,
    }));
  }, [data]);

  const supplierChart = useMemo(() => {
    return (data.bySupplier || []).slice(0, 8).map((s) => ({
      nome: (s.fornecedor || "").length > 22 ? s.fornecedor.slice(0, 20) + "…" : s.fornecedor,
      total: s.total_valor || 0,
      qtd: s.qtd_compras || 0,
    }));
  }, [data]);

  const dataHoje = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

  const doPrint = () => window.print();

  if (loading) {
    return <div className="text-center py-24 font-mono-data text-sm text-[#777]">Carregando dados da Assembleia...</div>;
  }

  return (
    <div data-testid="assembleia-report" className="assembleia-root">
      {/* Print-optimized styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .assembleia-noprint { display: none !important; }
          .assembleia-root { background: white !important; }
          .assembleia-page-break { page-break-before: always; break-before: page; }
          .assembleia-avoid-break { page-break-inside: avoid; break-inside: avoid; }
        }
        .assembleia-root { background: ${COL.paper}; }
        .assembleia-hero {
          background: linear-gradient(135deg, ${COL.ink} 0%, ${COL.primary} 60%, #2A7A54 100%);
          color: white;
          position: relative;
          overflow: hidden;
        }
        .assembleia-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(circle at 85% 20%, rgba(212, 160, 23, 0.18) 0%, transparent 40%),
            radial-gradient(circle at 15% 90%, rgba(255, 122, 0, 0.14) 0%, transparent 45%),
            repeating-linear-gradient(45deg, transparent 0 22px, rgba(255,255,255,0.02) 22px 23px);
          pointer-events: none;
        }
        .assembleia-kpi {
          border: 1px solid rgba(11, 46, 31, 0.12);
          background: white;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          position: relative;
          overflow: hidden;
        }
        .assembleia-kpi:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px -12px rgba(11, 46, 31, 0.28);
        }
        .assembleia-kpi::after {
          content: '';
          position: absolute;
          top: 0; right: 0;
          width: 80px; height: 80px;
          background: radial-gradient(circle at top right, var(--accent-col, ${COL.primary}) 0%, transparent 70%);
          opacity: 0.08;
        }
        .assembleia-card {
          background: white;
          border: 1px solid rgba(11, 46, 31, 0.10);
          box-shadow: 0 1px 2px rgba(11, 46, 31, 0.04);
        }
        .assembleia-panel-title {
          font-family: 'Space Grotesk', system-ui, sans-serif;
          letter-spacing: -0.01em;
        }
        .rule-thin {
          height: 1px;
          background: linear-gradient(to right, ${COL.primary}, transparent);
        }
      `}</style>

      {/* HEADER hero */}
      <div className="assembleia-hero p-8 md:p-10 rounded-[2px] mb-8 assembleia-avoid-break">
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 bg-white/10 backdrop-blur border border-white/20 rounded-full">
              <Sparkles className="w-3.5 h-3.5" />
              <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] font-bold">Relatório da Assembleia · Exercício {year}</span>
            </div>
            <h1 className="assembleia-panel-title text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-2">
              Prestação de Contas<br />
              <span style={{ color: COL.gold }}>COOMFLONA</span>
            </h1>
            <p className="max-w-2xl text-sm md:text-base text-white/85 leading-relaxed">
              Panorama consolidado das movimentações, investimentos, compras e atendimento aos cooperados
              da Cooperativa Mista da Floresta Nacional do Tapajós no exercício <b>{year}</b>.
            </p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-mono-data uppercase tracking-widest text-white/70">
              <span>Emitido em <b className="text-white">{dataHoje}</b></span>
              <span>Responsável: <b className="text-white">{user?.name || "—"}</b></span>
            </div>
          </div>
          <div className="assembleia-noprint flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              data-testid="assembleia-year"
              className="bg-white/10 backdrop-blur border-2 border-white/30 text-white px-4 py-2 font-mono-data text-sm outline-none"
            >
              {YEARS().map((y) => <option key={y} value={y} className="text-black">Exercício {y}</option>)}
            </select>
            <button
              onClick={doPrint}
              data-testid="assembleia-print"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0B2E1F] font-display font-bold uppercase text-xs tracking-widest px-5 py-2.5 hover:bg-[#D4A017] hover:text-white transition-colors"
            >
              <Printer className="w-4 h-4" /> Imprimir · PDF
            </button>
          </div>
        </div>
      </div>

      {/* KPIs Hero */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPI label="Investimento Total" value={fmtBRL(kpis.totalCompras)} sub="Compras + comunidade (12m)" icon={DollarSign} color={COL.primary} accent testid="kpi-invest" />
        <KPI label="Valor em Estoque" value={fmtBRL(kpis.valorEstoque)} sub={`${kpis.qtdProdutos} produtos ativos`} icon={Package} color={COL.accent} testid="kpi-estoque" />
        <KPI label="Movimentações" value={kpis.totalMov.toLocaleString("pt-BR")} sub={`${kpis.totalReqs} requisições no período`} icon={Activity} color={COL.leaf} testid="kpi-mov" />
        <KPI label="Atendimentos" value={kpis.totalCooperados.toLocaleString("pt-BR")} sub={`${kpis.qtdComunidades} comunidades atendidas`} icon={Users} color={COL.gold} testid="kpi-atend" />
      </div>

      {/* Section 1: Investimento por mês */}
      <Section title="Evolução do Investimento" subtitle="Compras registradas por fornecedores e comunidades ao longo dos meses" icon={TrendingUp}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 assembleia-card p-5 assembleia-avoid-break">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={purchaseChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradForn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COL.primary} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={COL.primary} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gradCom" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COL.accent} stopOpacity={0.7} />
                    <stop offset="100%" stopColor={COL.accent} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#64748b" />
                <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#64748b"
                       tickFormatter={(v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`} />
                <Tooltip
                  formatter={(v) => fmtBRL(v)}
                  contentStyle={{ background: COL.paper, border: `1px solid ${COL.primary}`, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.05em" }} />
                <Area type="monotone" dataKey="Fornecedor" stroke={COL.primary} strokeWidth={2} fill="url(#gradForn)" />
                <Area type="monotone" dataKey="Comunidade" stroke={COL.accent} strokeWidth={2} fill="url(#gradCom)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="assembleia-card p-5 assembleia-avoid-break flex flex-col">
            <h4 className="font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] mb-4">Distribuição por Categoria</h4>
            {categoryChart.length === 0 ? (
              <div className="text-sm text-[#94A3B8] font-mono-data text-center py-12">Sem dados de estoque.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={categoryChart} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {categoryChart.map((_, i) => <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtBRL(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="mt-3 space-y-1.5 text-xs">
                  {categoryChart.map((c, i) => (
                    <li key={i} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-3 h-3 shrink-0" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                        <span className="truncate">{c.name}</span>
                      </span>
                      <span className="font-mono-data font-bold shrink-0" style={{ color: COL.slate }}>{fmtBRL(c.value)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </Section>

      {/* Section 2: Comunidades atendidas */}
      <Section title="Comunidades Atendidas" subtitle="Ranking das comunidades por volume de compras registradas" icon={Trees}>
        <div className="assembleia-card p-6 assembleia-avoid-break">
          {communityChart.length === 0 ? (
            <div className="text-sm text-[#94A3B8] font-mono-data text-center py-12">Nenhuma compra comunitária no período.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(280, communityChart.length * 36)}>
              <BarChart data={communityChart} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#64748b"
                       tickFormatter={(v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`} />
                <YAxis type="category" dataKey="nome" width={160} tick={{ fontSize: 11 }} stroke={COL.slate} />
                <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: COL.paper, border: `1px solid ${COL.primary}`, fontSize: 12 }} />
                <Bar dataKey="total" fill={COL.leaf} radius={[0, 4, 4, 0]}>
                  {communityChart.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? COL.gold : i < 3 ? COL.primary : COL.leaf} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Section>

      {/* Section 3: Top produtos e fornecedores */}
      <Section title="Consumo & Fornecedores" subtitle="Produtos mais movimentados e principais parceiros comerciais" icon={ShoppingBag}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="assembleia-card p-6 assembleia-avoid-break">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-4 h-4" style={{ color: COL.accent }} />
              <h4 className="font-mono-data text-[10px] uppercase tracking-widest text-[#64748b]">Top 10 · Produtos Mais Saídos</h4>
            </div>
            {topProductsChart.length === 0 ? (
              <div className="text-sm text-[#94A3B8] font-mono-data text-center py-12">Sem movimentações.</div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={topProductsChart} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#64748b" />
                  <YAxis type="category" dataKey="nome" width={160} tick={{ fontSize: 10 }} stroke={COL.slate} />
                  <Tooltip contentStyle={{ background: COL.paper, border: `1px solid ${COL.primary}`, fontSize: 12 }} />
                  <Bar dataKey="total" fill={COL.accent} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="assembleia-card p-6 assembleia-avoid-break">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4" style={{ color: COL.primary }} />
              <h4 className="font-mono-data text-[10px] uppercase tracking-widest text-[#64748b]">Top Fornecedores · Volume de Compras</h4>
            </div>
            {supplierChart.length === 0 ? (
              <div className="text-sm text-[#94A3B8] font-mono-data text-center py-12">Sem compras registradas.</div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={supplierChart} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#64748b"
                         tickFormatter={(v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`} />
                  <YAxis type="category" dataKey="nome" width={160} tick={{ fontSize: 10 }} stroke={COL.slate} />
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ background: COL.paper, border: `1px solid ${COL.primary}`, fontSize: 12 }} />
                  <Bar dataKey="total" fill={COL.primary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </Section>

      {/* Section 4: Resumo mensal em tabela — pronto pra imprimir */}
      <Section title="Detalhamento Mensal Consolidado" subtitle="Movimentação de valores mês a mês (últimos 12 meses)" icon={BarChart3}>
        <div className="assembleia-card p-4 assembleia-avoid-break overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b-2" style={{ borderColor: COL.primary }}>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b]">Mês</th>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] text-right">Entradas</th>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] text-right">Saídas</th>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] text-right">Saldo</th>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] text-right">C. Fornecedor</th>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] text-right">C. Comunidade</th>
                <th className="py-3 px-3 font-mono-data text-[10px] uppercase tracking-widest text-[#64748b] text-right">Requisições</th>
              </tr>
            </thead>
            <tbody>
              {(data.monthly || []).map((r, i) => (
                <tr key={r.mes_label} className={i % 2 === 0 ? "bg-[#F8FAF8]" : ""}>
                  <td className="py-2.5 px-3 font-bold font-mono-data" style={{ color: COL.primary }}>{r.mes_label}</td>
                  <td className="py-2.5 px-3 text-right font-mono-data" style={{ color: "#059669" }}>{fmtBRL(r.entrada_valor)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-data" style={{ color: COL.clay }}>{fmtBRL(r.saida_valor)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-data font-bold" style={{ color: (r.saldo_valor || 0) >= 0 ? "#059669" : "#dc2626" }}>{fmtBRL(r.saldo_valor)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-data">{fmtBRL(r.compras_fornecedor_valor)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-data">{fmtBRL(r.compras_comunidade_valor)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-data">{r.requisicoes}</td>
                </tr>
              ))}
              {(data.monthly || []).length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-[#94A3B8] font-mono-data text-sm">Sem dados no período.</td></tr>
              )}
            </tbody>
            {(data.monthly || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 font-bold" style={{ borderColor: COL.primary, background: COL.soft }}>
                  <td className="py-3 px-3 font-mono-data uppercase text-xs tracking-widest">Total</td>
                  <td className="py-3 px-3 text-right font-mono-data">{fmtBRL((data.monthly || []).reduce((s, r) => s + (r.entrada_valor || 0), 0))}</td>
                  <td className="py-3 px-3 text-right font-mono-data">{fmtBRL((data.monthly || []).reduce((s, r) => s + (r.saida_valor || 0), 0))}</td>
                  <td className="py-3 px-3 text-right font-mono-data">{fmtBRL((data.monthly || []).reduce((s, r) => s + (r.saldo_valor || 0), 0))}</td>
                  <td className="py-3 px-3 text-right font-mono-data">{fmtBRL((data.monthly || []).reduce((s, r) => s + (r.compras_fornecedor_valor || 0), 0))}</td>
                  <td className="py-3 px-3 text-right font-mono-data">{fmtBRL((data.monthly || []).reduce((s, r) => s + (r.compras_comunidade_valor || 0), 0))}</td>
                  <td className="py-3 px-3 text-right font-mono-data">{(data.monthly || []).reduce((s, r) => s + (r.requisicoes || 0), 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Section>

      {/* FOOTER */}
      <div className="mt-10 pt-6 border-t assembleia-avoid-break" style={{ borderColor: COL.primary }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] font-mono-data uppercase tracking-widest" style={{ color: COL.slate }}>
          <div>
            <b style={{ color: COL.primary }}>COOMFLONA</b> · Cooperativa Mista da Floresta Nacional do Tapajós
          </div>
          <div>Documento emitido em <b>{dataHoje}</b> · Sistema de Almoxarifado</div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, color, accent, testid }) {
  return (
    <div className="assembleia-kpi p-5 rounded-[2px]" style={{ "--accent-col": color }} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <span className="font-mono-data text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: COL.slate }}>{label}</span>
        <div className="w-9 h-9 flex items-center justify-center rounded-[2px]" style={{ background: `${color}18`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className={`font-black tracking-tight leading-none ${accent ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"}`} style={{ color: COL.ink, fontFamily: "'Space Grotesk', system-ui" }}>
        {value}
      </div>
      {sub && <div className="mt-2 font-mono-data text-[11px]" style={{ color: COL.muted }}>{sub}</div>}
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, children }) {
  return (
    <section className="mb-10 assembleia-avoid-break">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {Icon && <Icon className="w-4 h-4" style={{ color: COL.primary }} />}
            <span className="font-mono-data text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: COL.primary }}>Seção</span>
          </div>
          <h3 className="assembleia-panel-title text-xl md:text-2xl font-black tracking-tight" style={{ color: COL.ink }}>{title}</h3>
          {subtitle && <p className="text-sm mt-0.5" style={{ color: COL.slate }}>{subtitle}</p>}
        </div>
      </div>
      <div className="rule-thin mb-5" />
      {children}
    </section>
  );
}
